"use client";

import React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { Box, Paper } from "@mui/material";
import { formatPercent } from "@/utils/valueFormatters";
import { chartColors } from "@/theme/chartColors";
import { useTranslation } from "react-i18next";
import { Typography } from "./wrappers/Typography";

type TurnoverDemographicData = {
    all: number;
    gender: {
        male: number;
        female: number;
    };
    generation: {
        babyBoomer: number;
        genX: number;
        genY: number;
        genZ: number;
    };
};

type TurnoverByDemographicProps = {
    data: {
        your_dealership: TurnoverDemographicData;
        your_peers: TurnoverDemographicData;
        all_dealerships: TurnoverDemographicData;
    };
    isGroup?: boolean;
};

const TurnoverByDemographicChart: React.FC<TurnoverByDemographicProps> = ({
    data,
    isGroup = false,
}) => {
    const { t } = useTranslation();

    const labels = [
        "all",
        ...Object.keys(data.your_dealership.gender),
        ...Object.keys(data.your_dealership.generation),
    ].map((key, i) =>
        key === "all"
            ? t(`demographics.categories.all`)
            : i === 1
              ? t(`demographics.gender.${key}`)
              : i === 2
                ? t(`demographics.gender.${key}`)
                : t(`demographics.generations.${key}`)
    );

    const yourDealershipData = [
        data.your_dealership.all,
        ...Object.values(data.your_dealership.gender),
        ...Object.values(data.your_dealership.generation),
    ];
    const yourPeersData = [
        data.your_peers.all,
        ...Object.values(data.your_peers.gender),
        ...Object.values(data.your_peers.generation),
    ];
    const allDealershipsData = [
        data.all_dealerships.all,
        ...Object.values(data.all_dealerships.gender),
        ...Object.values(data.all_dealerships.generation),
    ];

    const chartData = {
        labels: labels,
        series: [
            {
                label: isGroup
                    ? t("common.comparisonGroups.yourGroup")
                    : t("common.comparisonGroups.yourDealership"),
                data: yourDealershipData,
            },
            {
                label: t("common.comparisonGroups.yourPeers"),
                data: yourPeersData,
            },
            {
                label: t("common.comparisonGroups.allDealerships"),
                data: allDealershipsData,
            },
        ],
    };

    return (
        <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: "1px solid #dee2e6" }}>
            <Typography
                variant="h6"
                component="h3"
                tx="executiveSummary.sections.turnoverByDemographic"
                sx={{ mb: 2, fontWeight: "bold", color: "#002046" }}
            />
            <Box height={400}>
                <BarChart
                    height={400}
                    xAxis={[
                        {
                            scaleType: "band",
                            data: chartData.labels,
                            valueFormatter: (value: string, context: { location: string }) => {
                                if (context.location === "tooltip") {
                                    return value;
                                } else {
                                    return value.replace(/\s*\([^)]*\)/g, "");
                                }
                            },
                        },
                    ]}
                    yAxis={[
                        {
                            valueFormatter: (value: number | null) =>
                                formatPercent((value ?? 0) * 100, 0),
                            max: 1,
                        },
                    ]}
                    series={chartData.series.map((s) => ({
                        data: s.data,
                        label: s.label,
                        valueFormatter: (value: number | null | undefined) =>
                            formatPercent(value ? value * 100 : undefined, 1),
                    }))}
                    colors={chartColors}
                    slotProps={{
                        legend: {
                            position: { vertical: "top", horizontal: "center" },
                        },
                    }}
                />
            </Box>
        </Paper>
    );
};

export default TurnoverByDemographicChart;
